import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import { fine, money } from '../../utils/format';
import { Chip, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/Toast';

interface Whereabouts {
  metals: {
    metalId: string;
    metalCode: string;
    metalName: string;
    total: string;
    byLocation: Record<string, string>;
  }[];
}

interface StockAccount {
  accountId: string | null;
  code: string;
  name: string;
  metalCode: string | null;
  purpose: string;
  books: string;
  stock: string;
  difference: string;
}

interface Alignment {
  lines: {
    code: string;
    name: string;
    metal: string | null;
    what: string;
    books: string;
    shouldBe: string;
    change: string;
  }[];
  entries: {
    code: string;
    name: string;
    debit: string;
    credit: string;
    why: string;
  }[];
  suspense: string;
  journalId: string | null;
  journalNo: string | null;
}

/** The places metal can be, in the order a jeweller thinks of them. */
const PLACES: { key: string; label: string; held: boolean }[] = [
  { key: 'IN_STOCK', label: 'On the shelf', held: true },
  { key: 'IN_LOTS', label: 'Old metal and scrap lots', held: true },
  { key: 'AT_MELTING', label: 'Out for melting', held: true },
  { key: 'AT_JOBWORK', label: 'With the karigar', held: true },
  { key: 'IN_TRANSIT', label: 'Between branches', held: true },
  { key: 'UNDER_REPAIR', label: 'Under repair', held: true },
  { key: 'SOLD', label: 'Sold (to date)', held: false },
  { key: 'MELTED', label: 'Melted (to date)', held: false },
  { key: 'RETURNED', label: 'Returned to suppliers', held: false },
  { key: 'WRITTEN_OFF', label: 'Written off', held: false },
];

const PURPOSE: Record<string, string> = {
  JEWELLERY: 'Finished pieces',
  BULLION: 'Bars and bullion',
  SCRAP: 'Old metal and scrap',
  AT_JOB_WORK: 'With karigars',
  IN_TRANSIT: 'Between branches',
  AT_MELTING: 'Out for melting',
};

const n = (v: string | null | undefined): number => Number(v || 0);

/**
 * Where every gram is, metal by metal — and whether the books agree.
 *
 * The first half is weight: pure grams on the shelf, in lots, out at the
 * refinery, with the karigar. The second is money: each stock account beside
 * the pieces and lots it is supposed to hold. When the two agree, the profit
 * figure is exact; when they do not, the difference is shown, and an owner
 * can bring the books into line with one dated, explained entry.
 */
export function MetalPositionPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const where = useQuery({
    queryKey: ['whereabouts'],
    queryFn: () => api.get<Whereabouts>('/stock/whereabouts'),
  });
  const rec = useQuery({
    queryKey: ['stock-reconciliation'],
    queryFn: () => api.get<{ data: StockAccount[] }>('/accounting/stock-reconciliation'),
    enabled: can('accounts.view'),
  });
  const preview = useQuery({
    queryKey: ['metal-books'],
    queryFn: () => api.get<Alignment>('/accounting/metal-books'),
    enabled: can('accounts.view'),
  });
  const apply = useMutation({
    mutationFn: () => api.post<Alignment>('/accounting/metal-books', {}),
    onSuccess: (r) => {
      toast('Books brought into line', r.journalNo ? `Entry ${r.journalNo}` : undefined);
      setConfirm(false);
      void client.invalidateQueries({ queryKey: ['stock-reconciliation'] });
      void client.invalidateQueries({ queryKey: ['metal-books'] });
    },
    onError: (e) => {
      setConfirm(false);
      setError(e);
    },
  });

  const accounts = rec.data?.data ?? [];
  const off = accounts.filter((a) => Math.abs(n(a.difference)) >= 0.01);
  const pending = preview.data?.lines ?? [];

  return (
    <>
      <PageHeader
        title="Where is the metal"
        subtitle="Every gram, metal by metal — and every stock account against what it holds."
      />
      <ErrorNotice error={where.error ?? rec.error ?? error} />
      {where.isLoading ? <Loading /> : null}

      <div className="grid gap-4 lg:grid-cols-3">
        {(where.data?.metals ?? []).map((m) => {
          const held = PLACES.filter((p) => p.held).reduce((a, p) => a + n(m.byLocation[p.key]), 0);
          return (
            <div key={m.metalId} className="rounded-md border border-silver-100 bg-surface p-4 shadow-card">
              <div className="mb-2 flex items-baseline justify-between">
                <h3 className="text-base font-semibold">{m.metalName}</h3>
                <span className="text-2xs text-ink-soft">fine grams</span>
              </div>
              <p className="num text-2xl font-semibold">{fine(held.toFixed(4))}</p>
              <p className="mb-3 text-2xs text-ink-soft">owned now, wherever it is</p>
              {PLACES.map((p) =>
                n(m.byLocation[p.key]) !== 0 ? (
                  <div
                    key={p.key}
                    className={`flex justify-between border-t border-silver-100/60 py-1 text-sm ${p.held ? '' : 'text-ink-soft'}`}
                  >
                    <span>{p.label}</span>
                    <span className="num">{fine(m.byLocation[p.key])}</span>
                  </div>
                ) : null,
              )}
            </div>
          );
        })}
      </div>

      {can('accounts.view') ? (
        <section className="mt-8">
          <div className="mb-2 flex items-end justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold">Stock in the books</h2>
              <p className="text-sm text-ink-soft">
                Each stock account beside the pieces and lots it holds, at their cost. Profit is exact only when every
                line agrees.
              </p>
            </div>
            {off.length === 0 && accounts.length > 0 ? <Chip tone="good">All agree</Chip> : null}
            {off.length > 0 ? <Chip tone="bad">{off.length} not agreeing</Chip> : null}
          </div>
          {rec.isLoading ? <Loading rows={3} /> : null}
          {accounts.length > 0 ? (
            <Table head={['Account', 'Metal', 'Holds', 'In the books', 'Stock on hand', 'Difference']}>
              {accounts.map((a) => (
                <tr key={a.code}>
                  <td className="td">
                    <span className="num mr-2 text-2xs text-ink-faint">{a.code}</span>
                    {a.name}
                  </td>
                  <td className="td">{a.metalCode ?? '—'}</td>
                  <td className="td text-sm">{PURPOSE[a.purpose] ?? a.purpose}</td>
                  <td className="td num text-right">{money(a.books)}</td>
                  <td className="td num text-right">{money(a.stock)}</td>
                  <td
                    className={`td num text-right ${Math.abs(n(a.difference)) >= 0.01 ? 'font-semibold text-danger' : 'text-ink-faint'}`}
                  >
                    {Math.abs(n(a.difference)) >= 0.01 ? money(a.difference) : '—'}
                  </td>
                </tr>
              ))}
            </Table>
          ) : null}

          {pending.length > 0 ? (
            <div className="mt-4 rounded-md border border-warn/40 bg-warn/5 p-4 text-sm">
              <h3 className="mb-1 font-semibold">These books were written before each metal had its own accounts</h3>
              <p className="mb-3 text-ink-soft">
                Bringing them into line posts one entry dated today: other metals’ sales and cost moved out of the gold
                accounts to their own, and each stock account set to the stock on hand.{' '}
                {n(preview.data?.suspense) !== 0
                  ? `The net difference of ${money(String(Math.abs(n(preview.data?.suspense))))} goes to Opening Balance Suspense (3900) for your accountant to settle — usually stock the shop already had when it started, tagged without a purchase bill.`
                  : 'Nothing goes to suspense — it is purely moving amounts to the right metal.'}
              </p>
              <Table head={['Account', 'Why', 'Debit', 'Credit']}>
                {(preview.data?.entries ?? []).map((l, i) => (
                  <tr key={i}>
                    <td className="td">
                      <span className="num mr-2 text-2xs text-ink-faint">{l.code}</span>
                      {l.name}
                    </td>
                    <td className="td text-sm">{l.why}</td>
                    <td className="td num text-right">{n(l.debit) > 0 ? money(l.debit) : ''}</td>
                    <td className="td num text-right">{n(l.credit) > 0 ? money(l.credit) : ''}</td>
                  </tr>
                ))}
              </Table>
              {can('document.void') ? (
                <button className="btn-primary mt-3" onClick={() => setConfirm(true)}>
                  Bring the books into line
                </button>
              ) : (
                <p className="mt-3 text-2xs text-ink-soft">The owner or an administrator can post this.</p>
              )}
            </div>
          ) : null}
        </section>
      ) : null}

      <ConfirmDialog
        open={confirm}
        title="Bring the metal books into line?"
        message="One journal entry, dated today, with every line explained. Nothing already posted is changed, and the entry can be reversed from the day book."
        confirmLabel="Post the entry"
        busy={apply.isPending}
        onCancel={() => setConfirm(false)}
        onConfirm={() => apply.mutate()}
      />
    </>
  );
}
